import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Portal,
  useOutsideClick,
  PopoverBody,
} from "@chakra-ui/react";
import React, { useCallback, useState, useRef } from "react";
import { useCloseWhenOutsideViewport } from "../../../hooks/useCloseWhenOutsideViewport";
import { useCurrentAccountStore } from "../../../store/currentAccountStore";
import { useQueryGetAccounts } from "../../../store/useQueryGetAccounts";
import { useOverlayPortalContainer } from "../../modals/OverlayPortalContainerContext";
import { AccountSwitcher } from "./AccountSwitcher";
import { CurrentAccountButton } from "./CurrentAccountButton";

export function OverlayAccountNav({ isCollapsed }: { isCollapsed: boolean }) {
  const queryGetAccounts = useQueryGetAccounts();
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);
  const currentAccount = queryGetAccounts.data?.accounts[accountId];

  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: contentRef,
    handler: (event) => {
      const target = event.target as Node | null;

      // a click into the trigger button should still not close the popover
      if (triggerRef.current?.contains(target)) {
        return;
      }

      handleClose();
    },
  });

  const portalContainerRef = useOverlayPortalContainer();

  useCloseWhenOutsideViewport({
    isOpen,
    targetRef: triggerRef,
    onClose: handleClose,
  });

  const offset = isCollapsed ? 16 : 12;

  return (
    <Popover
      placement="right-start"
      autoFocus={false}
      isOpen={isOpen}
      onOpen={() => handleOpen()}
      onClose={() => {
        handleClose();
      }}
      isLazy={false} // must be off to allow the add new account modal to persist
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, offset],
          },
        },
        {
          name: "preventOverflow",
          options: {
            boundary: "viewport",
            padding: 8,
            tether: false,
            altAxis: true,
          },
        },
      ]}
    >
      <PopoverTrigger>
        <CurrentAccountButton
          ref={triggerRef}
          currentAccount={currentAccount}
          isCollapsed={isCollapsed}
          onClick={() => {
            if (isOpen) {
              handleClose();
            } else {
              handleOpen();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              e.stopPropagation();

              if (!isOpen) {
                handleOpen();
              }
            }

            // allow the user to type into the button to open the
            // popover
            const isPrintableCharacter =
              e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

            if (isPrintableCharacter) {
              e.preventDefault();
              e.stopPropagation();

              if (!isOpen) {
                handleOpen();
              }
            }
          }}
          onBlur={(e) => {
            // close if the user clicks outside the popover, or if they
            // navigate away using the keyboard
            if (
              isOpen &&
              !triggerRef.current?.contains(e.relatedTarget) &&
              !contentRef.current?.contains(e.relatedTarget)
            ) {
              handleClose();
            }
          }}
        />
      </PopoverTrigger>
      <Portal containerRef={portalContainerRef}>
        {queryGetAccounts.data && currentAccount && (
          <PopoverContent
            ref={contentRef}
            role="listbox"
            zIndex="dropdown"
            maxH="calc(100dvh - 1rem)"
            maxW="25rem"
            minW={0}
            width="auto"
            overflow="hidden"
            border="1px"
            borderColor="gray.300"
            borderRadius="md"
            backgroundColor="gray.300"
            boxShadow="xl"
          >
            <PopoverBody display="flex" flex={1} minH={0} minW={0} p={0}>
              <AccountSwitcher
                accounts={queryGetAccounts.data.accounts}
                currentAccountId={currentAccount._id}
              />
            </PopoverBody>
          </PopoverContent>
        )}
      </Portal>
    </Popover>
  );
}
